import React, { useState, useEffect } from "react";
import axiosInstance from "../../../../common/axiosInstance";

const EmployeeTable = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [perPage, setPerPage] = useState(15);

  // Pagination Meta
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
  });

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get("/employees", {
        params: {
          page,
          search,
          status,
          sort_by: sortBy,
          sort_order: sortOrder,
          per_page: perPage,
        },
      });

      setEmployees(response.data.data || []);
      setMeta(response.data.meta || {});
    } catch (err) {
      console.error("Employee API Error:", err);

      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch employees."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [page, search, status, sortBy, sortOrder, perPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= meta.last_page) {
      setPage(newPage);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Employee Records</h2>

      {/* Search & Filter */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "20px",
        }}
      >
        <input
          type="text"
          placeholder="Search employees..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{
            padding: "8px",
            width: "250px",
          }}
        />

        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          style={{
            padding: "8px",
          }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending_verification">
            Pending Verification
          </option>
        </select>
      </div>

      {/* Loading */}
      {loading && <p>Loading employees...</p>}

      {/* Error */}
      {error && (
        <p style={{ color: "red", fontWeight: "bold" }}>
          {error}
        </p>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          <table
            border="1"
            cellPadding="10"
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th>Employee Code</th>
                <th>Full Name</th>
                <th>Official Email</th>
                <th>Phone</th>
                <th>Gender</th>
                <th>Status</th>
                <th>Remarks</th>
              </tr>
            </thead>

            <tbody>
              {employees.length > 0 ? (
                employees.map((emp) => (
                  <tr key={emp.uuid}>
                    <td>{emp.employee_code}</td>
                    <td>{emp.full_name}</td>
                    <td>{emp.official_email}</td>
                    <td>{emp.primary_phone}</td>
                    <td>{emp.gender}</td>
                    <td>{emp.status}</td>
                    <td>{emp.remarks || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" align="center">
                    No employees found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div
            style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              Showing {meta.from || 0} to {meta.to || 0} of{" "}
              {meta.total || 0} entries
            </div>

            <div
              style={{
                display: "flex",
                gap: "5px",
              }}
            >
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>

              {Array.from(
                { length: meta.last_page || 1 },
                (_, index) => index + 1
              ).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    fontWeight:
                      page === pageNum ? "bold" : "normal",
                  }}
                >
                  {pageNum}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === meta.last_page}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeTable;